import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Table,
  Badge,
} from "reactstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { ClipLoader } from "react-spinners";
import { getLedgerTransactions, postLedgerTransaction } from "../../components/Api/GmsLedgerPosting";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getAccountSubHeads } from "../../components/Api/GmsFinance";
import { hasPagePermissionWithAdmin } from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForDisplay, formatDateForAPI } from "../../helpers/dateHelper";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

// Get start and end of current month
const getStartOfCurrentMonth = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const getEndOfCurrentMonth = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;
  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
  };
};

export default function LedgerPostingPage() {
  const session = useMemo(() => getUserSession(), []);

  // Helper to get distinct color classes for each transaction type
  const getTransactionTypeStyle = (type) => {
    switch (type) {
      case "Sales":
      case "Cash Sales":
      case "Credit Sales":
        return { color: "soft-success", textClass: "text-success" };
      case "Sales Return":
      case "Cash Sales Return":
      case "Credit Sales Return":
        return { color: "soft-danger", textClass: "text-danger" };
      case "Purchase":
      case "Cash Purchase":
      case "Credit Purchase":
        return { color: "soft-primary", textClass: "text-primary" };
      case "Purchase Return":
      case "Cash Purchase Return":
      case "Credit Purchase Return":
        return { color: "soft-warning", textClass: "text-warning" };
      case "Customer Receipt":
        return { color: "soft-info", textClass: "text-info" };
      case "Supplier Payment":
      case "Supplier Payment (Legacy)":
        return { color: "soft-dark", textClass: "text-dark" };
      case "Petty Cash Expense":
        return { color: "soft-danger", textClass: "text-danger" };
      case "Petty Cash Receipt":
        return { color: "soft-success", textClass: "text-success" };
      case "Cash Deposit":
        return { color: "soft-info", textClass: "text-info" };
      default:
        return { color: "soft-primary", textClass: "text-primary" };
    }
  };
  
  // Permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsLedgerPosting');

  // Filter States
  const [fromDate, setFromDate] = useState(getStartOfCurrentMonth());
  const [toDate, setToDate] = useState(getEndOfCurrentMonth());
  const [isPosted, setIsPosted] = useState({ value: 0, label: "No" }); // Default filter to Unposted (No)
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // Master Data States
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingMaster, setLoadingMaster] = useState(false);

  // List & Pagination States
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Redirect if no permission
  useEffect(() => {
    if (!hasPermission) {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Load Businesses on Mount
  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
    }
  }, [hasPermission]);

  // Load Branches when Business changes
  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedBusiness]);

  // Initial load once business/branch defaults are set
  useEffect(() => {
    if (hasPermission && businesses.length > 0) {
      loadTransactions();
    }
  }, [businesses, selectedBusiness, selectedBranch]);

  // Infinite Scroll Listener
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.scrollHeight - 100
      ) {
        if (hasMore && !loadingMore && !loading) {
          loadMoreTransactions();
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadingMore, loading, pageNumber, transactions, totalCount]);

  const loadBusinesses = async () => {
    try {
      setLoadingMaster(true);
      const data = await getBusinesses({ business_Active: true });
      const businessData = Array.isArray(data) ? data : (data.data || []);
      const options = businessData.map(b => ({
        value: b.business_Id,
        label: b.business_NameEn
      }));
      setBusinesses(options);

      // Default to session business
      if (session.businessId) {
        const defaultBus = options.find(o => o.value === session.businessId);
        if (defaultBus) {
          setSelectedBusiness(defaultBus);
        }
      }
    } catch (error) {
      console.error("Error loading businesses:", error);
    } finally {
      setLoadingMaster(false);
    }
  };

  const loadBranches = async (businessId) => {
    try {
      setLoadingMaster(true);
      const data = await getBranches({ Business_Id: businessId, branch_Active: true });
      const branchData = Array.isArray(data) ? data : (data.data || []);
      const options = branchData.map(br => ({
        value: br.branch_Id,
        label: br.branch_NameEn
      }));
      setBranches(options);

      // Default to session branch
      if (session.branchId) {
        const defaultBranch = options.find(o => o.value === session.branchId);
        if (defaultBranch) {
          setSelectedBranch(defaultBranch);
        }
      } else {
        setSelectedBranch(null);
      }
    } catch (error) {
      console.error("Error loading branches:", error);
    } finally {
      setLoadingMaster(false);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setPageNumber(1);
      const filter = {
        FromDate: fromDate ? formatDateForAPI(fromDate) : null,
        ToDate: toDate ? formatDateForAPI(toDate) : null,
        IsPosted: isPosted ? (isPosted.value === "all" ? null : isPosted.value === 1) : null,
        BusinessId: selectedBusiness ? selectedBusiness.value : session.businessId,
        BranchId: selectedBranch ? selectedBranch.value : session.branchId,
        PageNumber: 1,
        PageSize: 50,
      };

      const data = await getLedgerTransactions(filter);
      const list = Array.isArray(data) ? data : [];
      setTransactions(list);
      
      const total = list[0]?.totalCount || 0;
      setTotalCount(total);
      setHasMore(list.length < total);
    } catch (error) {
      console.error("Error loading ledger transactions:", error);
      Swal.fire("Error", "Failed to retrieve transactions.", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadMoreTransactions = async () => {
    if (loadingMore || !hasMore) return;
    try {
      setLoadingMore(true);
      const nextPage = pageNumber + 1;
      const filter = {
        FromDate: fromDate ? formatDateForAPI(fromDate) : null,
        ToDate: toDate ? formatDateForAPI(toDate) : null,
        IsPosted: isPosted ? (isPosted.value === "all" ? null : isPosted.value === 1) : null,
        BusinessId: selectedBusiness ? selectedBusiness.value : session.businessId,
        BranchId: selectedBranch ? selectedBranch.value : session.branchId,
        PageNumber: nextPage,
        PageSize: 50,
      };

      const data = await getLedgerTransactions(filter);
      const list = Array.isArray(data) ? data : [];
      if (list.length > 0) {
        setTransactions(prev => [...prev, ...list]);
        setPageNumber(nextPage);
        const total = list[0]?.totalCount || totalCount;
        setTotalCount(total);
        setHasMore((transactions.length + list.length) < total);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading more ledger transactions:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleApplyFilters = () => {
    loadTransactions();
  };

  const handleClearFilters = () => {
    setFromDate(getStartOfCurrentMonth());
    setToDate(getEndOfCurrentMonth());
    setIsPosted({ value: 0, label: "No" });
    if (session.businessId) {
      const defaultBus = businesses.find(o => o.value === session.businessId);
      setSelectedBusiness(defaultBus || null);
    } else {
      setSelectedBusiness(null);
    }
    setSelectedBranch(null);
    
    // Trigger reloading with defaults
    setTimeout(() => {
      loadTransactions();
    }, 50);
  };

  // Real post action
  const handlePostClick = async (transaction) => {
    // If it's a Petty Cash transaction, ask for Account Sub-Head selection
    if (transaction.sourceTable === "CashLedger") {
      try {
        Swal.fire({
          title: "Fetching accounts...",
          didOpen: () => {
            Swal.showLoading();
          },
          allowOutsideClick: false,
          showConfirmButton: false,
        });

        const subHeads = await getAccountSubHeads();
        const activeSubHeads = subHeads.filter(s => s.status);

        if (activeSubHeads.length === 0) {
          Swal.fire("Warning", "No active Account Sub-Heads found in the system.", "warning");
          return;
        }

        const inputOptions = {};
        activeSubHeads.forEach(s => {
          inputOptions[s.accountSubHeadID] = `${s.accountSubHeadCode ? s.accountSubHeadCode + ' - ' : ''}${s.accountSubHeadNameEn}`;
        });

        Swal.fire({
          title: "Post Petty Cash Transaction",
          text: `Please select the Ledger Account (Account Sub-Head) to post this ${transaction.transactionType} of ${transaction.amount?.toFixed(2)}:`,
          input: "select",
          inputOptions: inputOptions,
          inputPlaceholder: "Choose Ledger Account",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Post to Ledger",
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (value) {
                resolve();
              } else {
                resolve("You must select a ledger account!");
              }
            });
          }
        }).then(async (result) => {
          if (result.isConfirmed && result.value) {
            try {
              Swal.fire({
                title: "Posting transaction...",
                didOpen: () => {
                  Swal.showLoading();
                },
                allowOutsideClick: false,
                showConfirmButton: false,
              });

              const res = await postLedgerTransaction(
                transaction.transactionId,
                transaction.sourceTable,
                parseInt(result.value)
              );
              if (!res.success) {
                throw new Error(res.message || "Failed to post transaction.");
              }

              Swal.fire({
                title: "Posted!",
                text: res.message || "Transaction has been posted successfully.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false
              });
              loadTransactions();
            } catch (error) {
              const errMsg = error.response?.data?.message || error.message || error;
              Swal.fire("Error", `Request failed: ${errMsg}`, "error");
            }
          }
        });

      } catch (error) {
        console.error("Error loading account sub-heads:", error);
        Swal.fire("Error", "Failed to retrieve account sub-heads.", "error");
      }
      return;
    }

    // Default flow for Sales, Purchases, Receipts
    Swal.fire({
      title: "Confirm Posting",
      text: `Are you sure you want to post transaction ${transaction.transactionNo} to the General Ledger?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Post it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          Swal.fire({
            title: "Posting transaction...",
            didOpen: () => {
              Swal.showLoading();
            },
            allowOutsideClick: false,
            showConfirmButton: false,
          });

          const res = await postLedgerTransaction(transaction.transactionId, transaction.sourceTable);
          if (!res.success) {
            throw new Error(res.message || "Failed to post transaction.");
          }

          Swal.fire({
            title: "Posted!",
            text: res.message || "Transaction has been posted successfully.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false
          });
          loadTransactions(); // Refresh the list
        } catch (error) {
          const errMsg = error.response?.data?.message || error.message || error;
          Swal.fire("Error", `Request failed: ${errMsg}`, "error");
        }
      }
    });
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Finance & Accounts" breadcrumbItem="Ledger Posting" />

          {/* Filters Card */}
          <Card>
            <CardBody>
              <h5 className="card-title mb-4">Filter Transactions</h5>
              <Row className="align-items-end">
                <Col lg={2} md={4} sm={6} className="mb-3">
                  <Label>From Date</Label>
                  <DatePicker
                    selected={fromDate}
                    onChange={(date) => setFromDate(date)}
                    className="form-control w-100"
                    placeholderText="Select start date"
                    dateFormat="dd/MM/yyyy"
                    isClearable
                  />
                </Col>

                <Col lg={2} md={4} sm={6} className="mb-3">
                  <Label>To Date</Label>
                  <DatePicker
                    selected={toDate}
                    onChange={(date) => setToDate(date)}
                    className="form-control w-100"
                    placeholderText="Select end date"
                    dateFormat="dd/MM/yyyy"
                    isClearable
                  />
                </Col>

                <Col lg={2} md={4} sm={6} className="mb-3">
                  <Label>Is Posted</Label>
                  <Select
                    value={isPosted}
                    onChange={(option) => setIsPosted(option)}
                    options={[
                      { value: "all", label: "All" },
                      { value: 1, label: "Yes" },
                      { value: 0, label: "No" },
                    ]}
                    styles={selectStyles}
                  />
                </Col>

                <Col lg={2} md={4} sm={6} className="mb-3">
                  <Label>Business</Label>
                  <Select
                    value={selectedBusiness}
                    onChange={(option) => setSelectedBusiness(option)}
                    options={businesses}
                    styles={selectStyles}
                    placeholder="Select Business"
                    isClearable
                  />
                </Col>

                <Col lg={2} md={4} sm={6} className="mb-3">
                  <Label>Branch</Label>
                  <Select
                    value={selectedBranch}
                    onChange={(option) => setSelectedBranch(option)}
                    options={branches}
                    styles={selectStyles}
                    placeholder="Select Branch"
                    isClearable
                  />
                </Col>

                <Col lg={2} md={4} sm={12} className="mb-3 text-end d-flex justify-content-end gap-2">
                  <Button color="primary" className="w-50" onClick={handleApplyFilters}>
                    Apply
                  </Button>
                  <Button color="secondary" className="w-50" onClick={handleClearFilters}>
                    Clear
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Transactions List */}
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="card-title mb-0">Transaction Ledger</h5>
                <span className="text-muted">Total Records: {totalCount}</span>
              </div>

              {loading && pageNumber === 1 ? (
                <div className="text-center py-5">
                  <ClipLoader color="#556ee6" size={50} />
                  <p className="mt-2 text-muted">Fetching transactions...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table table-bordered table-striped align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "60px" }}>Sl No</th>
                        <th>Date & Time</th>
                        <th>Transaction No</th>
                        <th>Transaction Type</th>
                        <th>Transactor</th>
                        <th>Transactor Type</th>
                        <th className="text-end">Amount</th>
                        <th className="text-end">VAT Amount</th>
                        <th className="text-center">Posted</th>
                        <th className="text-center" style={{ width: "120px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.length > 0 ? (
                        transactions.map((tx, idx) => (
                          <tr key={`${tx.sourceTable}-${tx.transactionId}`}>
                            <td>{idx + 1}</td>
                            <td>{formatDateForDisplay(tx.transactionDate, 'date')}</td>
                            <td>
                              <span className="font-weight-medium">{tx.transactionNo}</span>
                            </td>
                            <td>
                              {(() => {
                                const style = getTransactionTypeStyle(tx.transactionType);
                                return (
                                  <Badge
                                    color={style.color}
                                    className={`${style.textClass} font-size-11 px-2 py-1`}
                                  >
                                    {tx.transactionType}
                                  </Badge>
                                );
                              })()}
                            </td>
                            <td>{tx.transactorName || "-"}</td>
                            <td>{tx.transactorType}</td>
                            <td className="text-end font-size-14 font-weight-semibold">
                              {tx.amount?.toFixed(2)}
                            </td>
                            <td className="text-end text-muted">
                              {tx.vatAmount?.toFixed(2)}
                            </td>
                            <td className="text-center">
                              {tx.isPosted ? (
                                <Badge color="soft-success" className="text-success font-size-11 px-2 py-1" pill>
                                  Yes
                                </Badge>
                              ) : (
                                <Badge color="soft-secondary" className="text-secondary font-size-11 px-2 py-1" pill>
                                  No
                                </Badge>
                              )}
                            </td>
                            <td className="text-center">
                              <Button
                                color="primary"
                                size="sm"
                                disabled={tx.isPosted}
                                onClick={() => handlePostClick(tx)}
                              >
                                {tx.isPosted ? "Posted" : "Post"}
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={10} className="text-center py-4 text-muted">
                            No transactions found for the selected criteria.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Infinite scroll status and load-more controls */}
              {loadingMore && (
                <div className="text-center py-3">
                  <ClipLoader color="#556ee6" size={30} />
                  <span className="ms-2 text-muted font-size-13">Loading more transactions...</span>
                </div>
              )}

              {hasMore && !loadingMore && !loading && transactions.length > 0 && (
                <div className="text-center py-3">
                  <Button color="light" size="sm" onClick={loadMoreTransactions}>
                    Load More
                  </Button>
                </div>
              )}

              {!hasMore && transactions.length > 0 && (
                <div className="text-center py-3 text-muted font-size-13">
                  <i className="mdi mdi-check-all me-1"></i> All transactions loaded
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
}
